import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge, Dropdown, Empty, Spin } from 'antd'
import { FaBell, FaCheck, FaTrash, FaEye, FaClock, FaImages } from 'react-icons/fa'
import { useNotifications } from '../../contexts/notification-context'
import './NotificationBell.css'

const NotificationBell = () => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications()

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    }
  }, [isOpen, fetchNotifications])

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'batch_complete':
        return <FaImages className="text-green-400" />
      case 'batch_failed':
        return <FaImages className="text-red-400" />
      default:
        return <FaBell className="text-blue-400" />
    }
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notificationId)
    }

    // Navigate based on notification type
    if (notification.batchJobId) {
      navigate('/batch-jobs')
    } else if (notification.designId) {
      navigate(`/designs?id=${notification.designId}`)
    }

    setIsOpen(false)
  }

  const dropdownContent = (
    <div className="notification-dropdown bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-80 max-h-96 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <h3 className="text-white font-semibold">Notifications</h3>
        {unreadCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              markAllAsRead()
            }}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Notification List */}
      <div className="notification-list overflow-y-auto max-h-72">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Spin size="small" />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            description={<span className="text-gray-500 dark:text-muted">No notifications</span>}
            className="py-8"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          notifications.slice(0, 10).map((notification) => (
            <div
              key={notification.notificationId}
              className={`notification-item flex items-stretch border-b border-gray-800 transition-colors ${notification.read ? 'bg-transparent' : 'bg-blue-500/5'
                } hover:bg-gray-800`}
            >
              <button
                type="button"
                aria-label={`${notification.read ? '' : 'Unread: '}${notification.title || 'Notification'}`}
                onClick={() => handleNotificationClick(notification)}
                className="flex-1 min-w-0 text-left p-3 flex items-start gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400 rounded-l"
              >
                <span className="notification-icon mt-1 flex-shrink-0">
                  {getNotificationIcon(notification.type)}
                </span>
                <span className="flex-1 min-w-0 block">
                  <span className="flex items-start justify-between gap-2">
                    <span className={`text-sm block ${notification.read ? 'text-gray-300' : 'text-white font-medium'}`}>
                      {notification.title}
                    </span>
                    {!notification.read && (
                      <span aria-hidden="true" className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                    )}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-muted mt-0.5 block line-clamp-2">
                    {notification.message}
                  </span>
                  <span className="flex items-center gap-2 mt-1">
                    <FaClock className="text-gray-500 dark:text-muted text-xs" aria-hidden="true" />
                    <span className="text-xs text-gray-500 dark:text-muted">
                      {formatTime(notification.createdAt)}
                    </span>
                  </span>
                </span>
              </button>
              <button
                type="button"
                aria-label={`Delete notification: ${notification.title || ''}`}
                onClick={() => deleteNotification(notification.notificationId)}
                className="flex-shrink-0 px-3 text-gray-600 dark:text-content hover:text-red-400 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400"
              >
                <FaTrash className="text-xs" aria-hidden="true" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-2 border-t border-gray-700">
          <button
            onClick={() => {
              navigate('/batch-jobs')
              setIsOpen(false)
            }}
            className="w-full text-center text-sm text-blue-400 hover:text-blue-300 py-2 transition-colors"
          >
            View All Batch Jobs
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div ref={dropdownRef} className="notification-bell-container">
      <Dropdown
        dropdownRender={() => dropdownContent}
        trigger={['click']}
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottomRight"
      >
        <button
          type="button"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          aria-haspopup="true"
          aria-expanded={isOpen}
          className="notification-bell-button relative p-1.5 border border-transparent rounded-lg hover:bg-gray-700/50 transition-colors"
        >
          <Badge count={unreadCount} size="small" offset={[-2, 2]}>
            <FaBell className="text-xl text-gray-300 hover:text-white transition-colors" aria-hidden="true" />
          </Badge>
        </button>
      </Dropdown>
    </div>
  )
}

export default NotificationBell

